import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Paper,
  TextField,
  Checkbox,
  Link,
} from '@mui/material';
import {
  Add,
  LocationOn,
  CalendarToday,
  Description,
  Delete,
  Star,
  StarBorder,
  Home,
} from '@mui/icons-material';
import { FormControlLabel } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { CANCELLATION_CUTOFF_UI_DEFAULT, type WebsiteSettings } from '../types';
import ProviderRatingDialog from '../components/ProviderRatingDialog';
import BidComparisonTable from '../components/BidComparisonTable';
import PaymentMethodSetup from '../components/PaymentMethodSetup';
import { useProviderAgreementGate } from '../components/agreements/ProviderAgreementSigningDialog';

// Helper function to format UTC dates correctly (extract UTC components to avoid timezone shifts)
// When dates are stored as UTC midnight (e.g., 2026-01-12T00:00:00.000Z), they represent the calendar date
// We should extract UTC components directly, not convert to local time
const formatUTCDateString = (dateInput: Date | string): string => {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  // Extract UTC date components directly (these represent the calendar date)
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1; // getUTCMonth() returns 0-11, add 1 for display
  const day = date.getUTCDate();
  return `${month}/${day}/${year}`;
};

const MyRequestsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; requestId: string; requestTitle: string; requestStatus?: string }>({ open: false, requestId: '', requestTitle: '' });
  const [deleting, setDeleting] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [requestBids, setRequestBids] = useState<{[key: string]: any[]}>({});
  const [loadingBids, setLoadingBids] = useState<{[key: string]: boolean}>({});
  const [bidCounts, setBidCounts] = useState<{[key: string]: number}>({});
  const [acceptBidDialog, setAcceptBidDialog] = useState({ open: false, bid: null as any, request: null as any });
  const [seasonalContractConfirmDialog, setSeasonalContractConfirmDialog] = useState({ open: false, bid: null as any, request: null as any });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const { requestSignatures, agreementDialog } = useProviderAgreementGate();
  const [acceptingBid, setAcceptingBid] = useState(false);
  const [customerAgreement, setCustomerAgreement] = useState('');
  const [ratingDialog, setRatingDialog] = useState({ open: false, provider: null as any });
  const [bidError, setBidError] = useState('');
  const [paymentSetupDialog, setPaymentSetupDialog] = useState({ 
    open: false, 
    bidId: '', 
    customerName: '', 
    amount: 0,
    existingCustomerId: '' // NEW: For property-specific payment setup
  });
  const [hasPaymentMethod, setHasPaymentMethod] = useState<boolean | null>(null); // Track if customer has payment method
  const [cancellationCutoffText, setCancellationCutoffText] = useState(CANCELLATION_CUTOFF_UI_DEFAULT);

  useEffect(() => {
    fetchRequests();
    loadCustomerAgreement();
  }, []);

  useEffect(() => {
    const loadCutoff = async () => {
      try {
        const res = await apiService.get<WebsiteSettings>('/website-settings');
        if (res?.data?.cancellationCutoffDescription) setCancellationCutoffText(res.data.cancellationCutoffDescription);
      } catch {
        // keep default
      }
    };
    loadCutoff();
  }, []);

  // Check for payment method when accept bid dialog opens
  useEffect(() => {
    const checkPaymentMethod = async () => {
      if (acceptBidDialog.open && acceptBidDialog.bid) {
        try {
          const paymentMethodsResponse = await apiService.get('/payments/saved-methods');
          const paymentMethods = paymentMethodsResponse.data || [];
          const hasActive = paymentMethodsResponse.success && 
            Array.isArray(paymentMethods) && 
            paymentMethods.some((method: any) => method.status === 'active');
          setHasPaymentMethod(hasActive);
          console.log('[ACCEPT-BID-DIALOG] Payment method check:', { hasActive, methods: Array.isArray(paymentMethods) ? paymentMethods.length : 0 });
        } catch (err) {
          console.error('[ACCEPT-BID-DIALOG] Error checking payment method:', err);
          setHasPaymentMethod(null); // Unknown state
        }
      } else {
        setHasPaymentMethod(null);
      }
    };
    checkPaymentMethod();
  }, [acceptBidDialog.open, acceptBidDialog.bid]);

  const loadCustomerAgreement = async () => {
    try {
      const response = await apiService.get('/legal/customer-agreement');
      if (response.success && response.data) {
        setCustomerAgreement((response.data as any).agreement);
      } else {
        setCustomerAgreement('Customer agreement could not be loaded. Please contact support.');
      }
    } catch (err) {
      console.error('Failed to load customer agreement:', err);
      setCustomerAgreement('Customer agreement could not be loaded. Please contact support.');
    }
  };


  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/service-requests?userType=customer');
      
      if (response.success && response.data) {
        // The API returns data in a nested structure: { data: { serviceRequests: [...] } }
        const requestsData = (response.data as any).serviceRequests || [];
        
        console.log('[MY-REQUESTS] Fetched service requests from API:', requestsData.length);
        requestsData.forEach((req: any, index: number) => {
          console.log(`[MY-REQUESTS] Request ${index + 1}:`, {
            id: req._id,
            serviceTypeId: req.serviceType?.id,
            serviceTypeName: req.serviceType?.name,
            serviceTypeCategory: req.serviceType?.category,
            status: req.status,
            frequency: req.frequency,
            paymentSchedule: req.paymentSchedule,
            isSeasonalContract: req.frequency === 'seasonal-contract'
          });
        });
        
        // Show all service requests except completed/cancelled/expired ones
        // Expired service requests are shown separately with clear messaging
        const activeRequests = requestsData.filter((request: any) => 
          request.status !== 'completed' && request.status !== 'cancelled' && request.status !== 'pending_cancellation' && request.status !== 'expired_unaccepted'
        );
        
        // Separate expired requests for display
        const expiredRequests = requestsData.filter((request: any) => 
          request.status === 'expired_unaccepted'
        );
        
        if (expiredRequests.length > 0) {
          console.log(`[MY-REQUESTS] Found ${expiredRequests.length} expired service request(s) - will show separately`);
        }
        
        console.log('[MY-REQUESTS] Active requests after filtering:', activeRequests.length);
        activeRequests.forEach((req: any, index: number) => {
          console.log(`[MY-REQUESTS] Active Request ${index + 1}:`, {
            id: req._id,
            serviceTypeId: req.serviceType?.id,
            serviceTypeName: req.serviceType?.name,
            serviceTypeCategory: req.serviceType?.category
          });
        });
        
        setRequests(Array.isArray(activeRequests) ? activeRequests : []);
        
        // Fetch bid counts for all active requests
        if (Array.isArray(activeRequests) && activeRequests.length > 0) {
          fetchBidCounts(activeRequests);
        }
      } else {
        setError('Failed to load service requests');
        setRequests([]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load service requests');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBidCounts = async (requests: any[]) => {
    try {
      // Fetch bid counts for all requests in parallel
      const bidCountPromises = requests.map(async (request) => {
        try {
          const response = await apiService.get(`/bids?requestId=${request._id}`);
          if (response.success && response.data) {
            return { requestId: request._id, count: (response.data as any).bids?.length || 0 };
          }
          return { requestId: request._id, count: 0 };
        } catch (err) {
          console.error(`Failed to fetch bid count for request ${request._id}:`, err);
          return { requestId: request._id, count: 0 };
        }
      });

      const bidCountResults = await Promise.all(bidCountPromises);
      
      // Update bid counts state
      const newBidCounts: {[key: string]: number} = {};
      bidCountResults.forEach(result => {
        newBidCounts[result.requestId] = result.count;
      });
      setBidCounts(newBidCounts);
    } catch (err) {
      console.error('Failed to fetch bid counts:', err);
    }
  };

  const fetchBidsForRequest = async (requestId: string) => {
    try {
      setLoadingBids(prev => ({ ...prev, [requestId]: true }));
      const response = await apiService.get(`/bids?requestId=${requestId}`);
      if (response.success && response.data) {
        const bids = (response.data as any).bids || [];
        setRequestBids(prev => ({ ...prev, [requestId]: bids }));
        // Update the bid count as well
        setBidCounts(prev => ({ ...prev, [requestId]: bids.length }));
      }
    } catch (err: any) {
      console.error('Failed to fetch bids:', err);
    } finally {
      setLoadingBids(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'bidding':
        return 'info';
      case 'accepted':
        return 'success';
      case 'scheduled':
        return 'primary';
      case 'in-progress':
        return 'secondary';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'pending_cancellation':
        return 'warning'; // Warning color for pending cancellation (yellow/orange)
      case 'expired':
        return 'error'; // Expired bids - red/error color
      case 'expired_unaccepted':
        return 'error'; // Expired service requests - red/error color
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatServices = (serviceType: any) => {
    if (!serviceType?.name) return 'N/A';
    
    // If the name contains commas, it's a comma-separated list (old format)
    if (serviceType.name.includes(', ')) {
      return serviceType.name
        .split(', ')
        .map((s: string) => 
          s === 'mowing' ? 'Lawn Mowing' :
          s === 'edging' ? 'Trimming' :
          s === 'fertilizingWeedControl' ? 'Fertilizing/Weed Control' :
          s.charAt(0).toUpperCase() + s.slice(1)
        )
        .join(', ');
    }
    
    // Otherwise, it's already a formatted display name (new format)
    // Just return it as-is
    return serviceType.name;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatPropertyType = (propertyType: string) => {
    if (!propertyType) return 'N/A';
    
    const typeMap: { [key: string]: string } = {
      'single_residential': 'Single Family Residential',
      'multi_family_condo': 'Multi-Family / Condo',
      'commercial': 'Commercial',
      'vacant_lot': 'Vacant Lot',
      'hoa_managed': 'HOA-Managed Property',
    };
    
    return typeMap[propertyType] || propertyType;
  };

  const getServiceName = (service: string) => {
    switch (service) {
      case 'mowing': return 'Lawn Mowing';
      case 'edging': return 'Trimming';
      case 'fertilizingWeedControl': return 'Fertilizing/Weed Control';
      case 'snowRemoval': return 'Snow Removal';
      default: return service.charAt(0).toUpperCase() + service.slice(1);
    }
  };

  const getFrequencyLabel = (frequency: string) => {
    switch (frequency) {
      case 'weekly': return 'Weekly';
      case 'bi-weekly': return 'Bi-Weekly';
      case 'monthly': return 'Monthly';
      case 'per-instance': return 'Per Instance';
      case 'seasonal-contract': return 'Seasonal Contract';
      default: return frequency.charAt(0).toUpperCase() + frequency.slice(1).replace(/-/g, ' ');
    }
  };

  // Service categorization functions
  const getServiceCategory = (service: string) => {
    // Handle both old service names and new display names
    const serviceLower = service.toLowerCase();
    
    if (serviceLower.includes('mowing') || serviceLower.includes('trimming') || serviceLower.includes('edging') || 
        serviceLower.includes('fertilizing') || serviceLower.includes('weed control') || 
        service === 'mowing' || service === 'edging' || service === 'fertilizingWeedControl') {
      return 'lawn';
    }
    
    if (serviceLower.includes('snow') || service === 'snowRemoval') {
      return 'snow';
    }
    
    return 'other';
  };


  const handleViewBids = (requestId: string) => {
    // Clear any existing bid errors when viewing bids
    setBidError('');
    
    // Toggle bid visibility for this request
    if (requestBids[requestId]) {
      // If bids are already loaded, just toggle visibility
      setRequestBids(prev => ({ ...prev, [requestId]: [] }));
    } else {
      // Fetch bids for this request
      fetchBidsForRequest(requestId);
    }
  };

  const handleCancelRequest = (request: any) => {
    setDeleteDialog({
      open: true,
      requestId: request._id,
      requestTitle: formatServices(request.serviceType),
      requestStatus: request.status,
    });
    setCancellationReason(''); // Reset cancellation reason when opening dialog
  };

  const confirmCancel = async () => {
    try {
      setDeleting(true);
      const response = await apiService.post(`/service-requests/${deleteDialog.requestId}/cancel`, {
        cancellationReason: cancellationReason.trim() || undefined,
      });
      
      if (response.success) {
        // Remove the cancelled request from the list
        setRequests(prev => prev.filter(req => req._id !== deleteDialog.requestId));
        setDeleteDialog({ open: false, requestId: '', requestTitle: '', requestStatus: undefined });
        setCancellationReason(''); // Reset cancellation reason after successful cancellation
        setSuccess(response.message || 'Service request cancelled successfully');
        if ((response as any).warnings && Array.isArray((response as any).warnings) && (response as any).warnings.length > 0) {
          setSuccess(`${response.message || 'Service request cancelled successfully'}. ${(response as any).warnings.join(' ')}`);
        }
      } else {
        setError(response.error || 'Failed to cancel service request');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to cancel service request');
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteDialog({ open: false, requestId: '', requestTitle: '', requestStatus: undefined });
    setCancellationReason(''); // Reset cancellation reason when canceling dialog
  };


  // Handle accepting individual services or service bundles
  const handleAcceptIndividualService = (bid: any, services: any[], request: any) => {
    // Prevent accepting expired bids
    if (bid.status === 'expired') {
      setError('This bid has expired and can no longer be accepted. Please request fresh bids.');
      return;
    }
    
    // Check if this includes any lawn services - if so, bundle ALL lawn services
    const hasLawnServices = services.some(service => getServiceCategory(service.service) === 'lawn');
    
    let finalServices = services;
    
    if (hasLawnServices) {
      // If accepting any lawn service, automatically include ALL lawn services from this provider
      const allLawnServices = bid.servicePricing.filter((sp: any) => getServiceCategory(sp.service) === 'lawn');
      finalServices = allLawnServices;
    }
    
    // Create a modified bid with the final services
    const modifiedBid = {
      ...bid,
      servicePricing: finalServices,
      price: finalServices.reduce((sum, service) => sum + service.price, 0),
      estimatedDuration: finalServices.reduce((sum, service) => sum + service.duration, 0)
    };
    
    // Check if modified bid has seasonal contracts
    const hasSeasonalContract = modifiedBid.servicePricing && modifiedBid.servicePricing.some((sp: any) => 
      sp.isSeasonalContract || sp.frequency === 'seasonal-contract'
    );

    if (hasSeasonalContract) {
      // Show seasonal contract confirmation dialog first
      setSeasonalContractConfirmDialog({ open: true, bid: modifiedBid, request });
    } else {
      // No seasonal contract - proceed directly to accept bid dialog
      setAcceptBidDialog({ open: true, bid: modifiedBid, request });
      setAgreedToTerms(false);
    }
  };

  // Handle accepting lawn services bundle
  const handleAcceptServiceBundle = (bid: any, services: any[], request: any) => {
    handleAcceptIndividualService(bid, services, request);
  };

  // handleAcceptAllServices function removed - Snow Removal is hidden, only lawn services available

  const handleCancelAcceptBid = () => {
    setAcceptBidDialog({ open: false, bid: null, request: null });
    setAgreedToTerms(false);
  };


  const handleProceedToPayment = async () => {
    if (!agreedToTerms) {
      setError('You must agree to the terms and conditions to proceed');
      return;
    }

    if (!acceptBidDialog.bid || !acceptBidDialog.bid._id) {
      setError('Bid information is missing. Please try again.');
      return;
    }

    const agreementsSigned = await requestSignatures({ bidId: acceptBidDialog.bid._id });
    if (!agreementsSigned) return;

    setAcceptingBid(true);
    setError('');

    try {
      // Check if customer already has an active payment method
      console.log('[PAYMENT-SETUP] Checking for existing payment methods...');
      const paymentMethodsResponse = await apiService.get('/payments/saved-methods');
      
      console.log('[PAYMENT-SETUP] Payment methods response:', {
        success: paymentMethodsResponse.success,
        hasData: !!paymentMethodsResponse.data,
        dataType: Array.isArray(paymentMethodsResponse.data) ? 'array' : typeof paymentMethodsResponse.data,
        dataLength: Array.isArray(paymentMethodsResponse.data) ? paymentMethodsResponse.data.length : 'N/A',
        data: paymentMethodsResponse.data
      });

      const paymentMethods = paymentMethodsResponse.data || [];
      const hasActivePaymentMethod = paymentMethodsResponse.success && 
        Array.isArray(paymentMethods) && 
        paymentMethods.some((method: any) => method.status === 'active');

      console.log('[PAYMENT-SETUP] Has active payment method?', hasActivePaymentMethod);

      if (hasActivePaymentMethod) {
        // Customer has a payment method - accept bid directly
        console.log('[PAYMENT-SETUP] Active payment method found, accepting bid directly:', acceptBidDialog.bid._id);
        const response = await apiService.post('/bids/accept', {
          bidId: acceptBidDialog.bid._id,
          customerAgreementAccepted: true
        });

        if (response.success) {
          setAcceptBidDialog({ open: false, bid: null, request: null });
          setAgreedToTerms(false);
          setSuccess('Bid accepted successfully!');
          fetchRequests();
        } else {
          setError(response.error || 'Failed to accept bid. Please try again.');
          setAcceptingBid(false);
        }
      } else {
        // No payment method - show payment setup dialog
        console.log('[PAYMENT-SETUP] No active payment method found, opening payment setup dialog for bid:', acceptBidDialog.bid._id);
        setAcceptingBid(false); // Reset loading state before showing dialog
        setPaymentSetupDialog({
          open: true,
          bidId: acceptBidDialog.bid._id,
          customerName: `${user?.firstName} ${user?.lastName}`,
          amount: acceptBidDialog.bid.price,
          existingCustomerId: '' // Will be determined during payment setup if needed
        });
      }
    } catch (err: any) {
      console.error('[PAYMENT-SETUP] Error checking payment methods:', err);
      setAcceptingBid(false);
      // If check fails, try to accept bid directly (backend will validate payment method)
      // This prevents unnecessary payment setup dialog if payment method exists but check failed
      console.log('[PAYMENT-SETUP] Payment check failed, attempting direct bid acceptance (backend will validate)');
      try {
        const response = await apiService.post('/bids/accept', {
          bidId: acceptBidDialog.bid._id,
          customerAgreementAccepted: true
        });

        if (response.success) {
          setAcceptBidDialog({ open: false, bid: null, request: null });
          setAgreedToTerms(false);
          setSuccess('Bid accepted successfully!');
          fetchRequests();
        } else {
          // Backend says no payment method - show payment setup dialog
          if ((response as any).requiresPaymentSetup) {
            setPaymentSetupDialog({
              open: true,
              bidId: acceptBidDialog.bid._id,
              customerName: `${user?.firstName} ${user?.lastName}`,
              amount: acceptBidDialog.bid.price,
              existingCustomerId: ''
            });
          } else {
            setError(response.error || 'Failed to accept bid. Please try again.');
          }
        }
      } catch (acceptErr: any) {
        // If bid acceptance also fails, show error (don't show payment setup dialog)
        setError(acceptErr.message || 'Failed to accept bid. Please check your payment method and try again.');
      }
    }
  };


  const handleViewProviderRating = (provider: any) => {
    setRatingDialog({ open: true, provider });
  };

  const handleCloseRatingDialog = () => {
    setRatingDialog({ open: false, provider: null });
  };

  const handlePaymentSetupSuccess = async () => {
    setPaymentSetupDialog({ open: false, bidId: '', customerName: '', amount: 0, existingCustomerId: '' });
    
    // After payment setup, accept the bid
    if (!acceptBidDialog.bid || !acceptBidDialog.bid._id) {
      setError('Bid information is missing. Please try accepting the bid again.');
      return;
    }

    try {
      console.log('[PAYMENT-SETUP] Payment setup successful, accepting bid:', acceptBidDialog.bid._id);
      const response = await apiService.post('/bids/accept', {
        bidId: acceptBidDialog.bid._id,
        customerAgreementAccepted: true
      });

      if (response.success) {
        // Bid accepted successfully
        setAcceptBidDialog({ open: false, bid: null, request: null });
        setAgreedToTerms(false);
        setSuccess('Payment method set up and bid accepted successfully!');
        
        // Refresh the requests to show updated status
        fetchRequests();
      } else {
        setError(response.error || 'Failed to accept bid after payment setup. Please try again.');
      }
    } catch (err: any) {
      // If bid is already accepted or no longer available, that's fine
      if (err.message?.includes('no longer available') || err.message?.includes('already accepted')) {
        setAcceptBidDialog({ open: false, bid: null, request: null });
        setAgreedToTerms(false);
        setSuccess('Payment method set up successfully!');
        fetchRequests();
      } else {
        setError(err.message || 'Failed to accept bid after payment setup. Please try accepting the bid again.');
      }
    }
  };

  const handlePaymentSetupClose = () => {
    setPaymentSetupDialog({ open: false, bidId: '', customerName: '', amount: 0, existingCustomerId: '' });
  };


  const renderStarRating = (rating: number, onClick?: () => void, size: 'small' | 'medium' = 'small') => {
    const stars = [];
    const starSize = size === 'small' ? 16 : 20;
    
    for (let i = 1; i <= 5; i++) {
      stars.push(
        i <= Math.round(rating) ? (
          <Star key={i} sx={{ fontSize: starSize, color: '#ffc107' }} />
        ) : (
          <StarBorder key={i} sx={{ fontSize: starSize, color: '#e0e0e0' }} />
        )
      );
    }
    
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': onClick ? { opacity: 0.7 } : {}
        }}
        onClick={onClick}
      >
        {stars}
        <Typography variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>
          ({rating.toFixed(1)})
        </Typography>
      </Box>
    );
  };

  if (loading) {
    return (
      <Container maxWidth={false} sx={{ py: 4, px: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ py: 4, px: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            My Service Requests
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View your current and past service requests
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/request-service')}
          sx={{ height: 'fit-content' }}
        >
          Submit New Request
        </Button>
      </Box>

      {/* Urgency Notice */}
      {Array.isArray(requests) && requests.length > 0 && requests.some((req: any) => req.status === 'pending' || req.status === 'bidding') && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>⏰ Important: Bid Expiration & Availability</AlertTitle>
          <Typography variant="body2">
            <strong>Provider bids are only available for 7 days</strong> after submission. Additionally, providers can delete their bids at any time if they become too busy. 
            We recommend reviewing and accepting bids promptly to secure your preferred provider and pricing.
          </Typography>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Service Requests */}
      {!Array.isArray(requests) || requests.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No service requests yet
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Get started by submitting your first service request
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/request-service')}
          >
            Submit Your First Request
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {Array.isArray(requests) && requests.map((request) => {
            const shouldShowDelete = request.status !== 'completed' && request.status !== 'cancelled' && request.status !== 'pending_cancellation';
            return (
            <Grid item xs={12} key={request._id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Status and Date */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Chip
                        label={request.status.toUpperCase()}
                        color={getStatusColor(request.status) as any}
                        size="small"
                      />
                      {shouldShowDelete && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => handleCancelRequest(request)}
                          sx={{ mt: 0, width: 'fit-content' }}
                        >
                          Cancel Service Request
                        </Button>
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(request.createdAt)}
                    </Typography>
                  </Box>

                  {/* Services */}
                  <Typography variant="h6" gutterBottom>
                    {formatServices(request.serviceType)}
                  </Typography>

                  {/* Seasonal Contract Info */}
                  {(() => {
                    // Debug logging - make it very visible
                    const isSeasonal = request.frequency === 'seasonal-contract';
                    console.log('🔍 [MY-REQUESTS] SEASONAL CONTRACT CHECK:', {
                      requestId: request._id,
                      serviceName: request.serviceType?.name,
                      frequency: request.frequency,
                      paymentSchedule: request.paymentSchedule,
                      isSeasonalContract: isSeasonal,
                      hasAcceptedBid: !!request.acceptedBidId,
                      fullRequest: request
                    });
                    return null;
                  })()}
                  {request.frequency === 'seasonal-contract' && (
                    (() => {
                      // Check if bid has been accepted (has full contract details)
                      if (request.acceptedBidId && request.acceptedBidId.servicePricing && Array.isArray(request.acceptedBidId.servicePricing)) {
                        // AFTER BID ACCEPTANCE: Show full contract details
                        const serviceCategory = request.serviceType?.category;
                        const matchingServicePricing = request.acceptedBidId.servicePricing.find((sp: any) => {
                          if (serviceCategory === 'mowing') {
                            return sp.service === 'lawnMowingTrimming' || sp.serviceName?.toLowerCase().includes('mowing');
                          } else if (serviceCategory === 'fertilizing') {
                            return sp.service === 'weedControlFertilizing' || sp.serviceName?.toLowerCase().includes('fertiliz') || sp.serviceName?.toLowerCase().includes('weed');
                          } else if (serviceCategory === 'snow_removal') {
                            return sp.service === 'snowRemoval' || sp.serviceName?.toLowerCase().includes('snow');
                          }
                          return false;
                        });
                        
                        const isSeasonalContract = matchingServicePricing?.isSeasonalContract || matchingServicePricing?.frequency === 'seasonal-contract';
                        const seasonalDetails = matchingServicePricing?.seasonalContractDetails;
                        const paymentSchedule = seasonalDetails?.paymentSchedule || 'single-payment';
                        
                        // Calculate monthly payment info if monthly
                        let monthlyPaymentInfo: { monthlyAmount: number; firstPaymentDate: Date; billingMonths: number } | null = null;
                        if (paymentSchedule === 'monthly' && seasonalDetails?.totalContractPrice && seasonalDetails?.contractStartDate && seasonalDetails?.contractEndDate) {
                          const contractStart = new Date(seasonalDetails.contractStartDate);
                          const contractEnd = new Date(seasonalDetails.contractEndDate);
                          const monthsDiff = (contractEnd.getFullYear() - contractStart.getFullYear()) * 12 +
                                            (contractEnd.getMonth() - contractStart.getMonth());
                          const billingMonths = Math.max(1, monthsDiff);
                          const monthlyAmount = seasonalDetails.totalContractPrice / billingMonths;
                          monthlyPaymentInfo = {
                            monthlyAmount,
                            firstPaymentDate: contractStart,
                            billingMonths
                          };
                        }
                        
                        if (isSeasonalContract && seasonalDetails) {
                          return (
                            <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'primary.50', borderRadius: 1, border: 1, borderColor: 'primary.200' }}>
                              <Typography variant="body2" fontWeight="bold" color="primary.dark" gutterBottom>
                                Seasonal Contract
                              </Typography>
                              
                              {/* Contract Dates */}
                              {seasonalDetails.contractStartDate && seasonalDetails.contractEndDate && (
                                <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                                  <strong>Contract Period:</strong> {formatUTCDateString(seasonalDetails.contractStartDate)} - {formatUTCDateString(seasonalDetails.contractEndDate)}
                                </Typography>
                              )}
                              
                              {/* Total Contract Price */}
                              {seasonalDetails.totalContractPrice && (
                                <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                                  <strong>Total Contract Price:</strong> ${seasonalDetails.totalContractPrice.toFixed(2)}
                                </Typography>
                              )}
                              
                              {/* Payment Schedule */}
                              {paymentSchedule === 'monthly' && monthlyPaymentInfo ? (
                                <Box sx={{ mt: 1, p: 1, backgroundColor: 'white', borderRadius: 0.5 }}>
                                  <Typography variant="body2" fontWeight="medium" color="primary.dark" gutterBottom>
                                    Monthly Payment Plan
                                  </Typography>
                                  <Typography variant="body2" color="text.primary">
                                    <strong>${monthlyPaymentInfo.monthlyAmount.toFixed(2)}</strong> per month
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    First Payment: <strong>{formatUTCDateString(monthlyPaymentInfo.firstPaymentDate)}</strong>
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.primary" sx={{ mt: 0.5 }}>
                                  <strong>Payment Schedule:</strong> One-Time Payment (Full payment after first service)
                                </Typography>
                              )}
                            </Box>
                          );
                        }
                      }
                      
                      // BEFORE BID ACCEPTANCE: Show minimal info (just seasonal contract + payment schedule)
                      const paymentSchedule = request.paymentSchedule || 'single-payment';
                      const paymentScheduleText = paymentSchedule === 'monthly' 
                        ? 'Monthly Payment' 
                        : 'One-Time Payment';
                      
                      return (
                        <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'primary.50', borderRadius: 1, border: 1, borderColor: 'primary.200' }}>
                          <Typography variant="body2" fontWeight="bold" color="primary.dark">
                            Seasonal Contract - {paymentScheduleText}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            Contract details will be available after accepting a bid
                          </Typography>
                        </Box>
                      );
                    })()
                  )}

                  {/* Address */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {request.address?.street}, {request.address?.city}, {request.address?.state}
                    </Typography>
                  </Box>

                  {/* Property Type */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Home sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Property Type: {formatPropertyType(request.propertyType)}
                    </Typography>
                  </Box>

                  {/* Requested Date */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Requested for: {formatDate(request.requestedDate)}
                    </Typography>
                  </Box>

                  {/* Special Instructions */}
                  {request.description && 
                   request.description !== 'No special instructions' && 
                   !request.description.startsWith('Services requested:') && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Description sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" fontWeight="medium">
                          Special Instructions:
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ pl: 3 }}>
                        {request.description}
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* Service Accepted Message */}
                  {request.status === 'accepted' && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>🎉 Service Accepted!</strong><br/>
                        Your service request has been accepted and payment method has been set up. 
                        Your provider will contact you shortly to schedule your services.
                      </Typography>
                    </Alert>
                  )}

                  {/* Service Request Expired Message */}
                  {request.status === 'expired_unaccepted' && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>⚠️ Service Request Expired</strong><br/>
                        Your service request has expired after 30 days without an accepted bid. 
                        Please submit a new service request to receive fresh bids from providers.
                      </Typography>
                    </Alert>
                  )}

                  {/* Current Bids Section - Only show for pending/bidding requests */}
                  {(request.status === 'pending' || request.status === 'bidding') && requestBids[request._id] && requestBids[request._id].length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight="medium" sx={{ mb: 2 }}>
                        Current Bids ({requestBids[request._id].length}):
                      </Typography>
                      
                      {/* Display bid errors */}
                      {bidError && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBidError('')}>
                          {bidError}
                        </Alert>
                      )}
                      
                      {/* Customer Selection Instructions */}
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>How to Select Providers:</strong><br/>
                          • <strong>Lawn Services</strong> (mowing, edging, weed control) are automatically bundled together<br/>
                          • <strong>Snow Removal</strong> will be available starting September 2025<br/>
                          • You can set up payment for your lawn services now
                        </Typography>
                      </Alert>

                      {/* Payment Status Alert */}
                      {false && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            <strong>✅ Payment Method Ready:</strong> You already have a payment method set up. 
                            You can accept bids directly without setting up payment again.
                          </Typography>
                        </Alert>
                      )}

                      {/* Snow Removal Reminder - Temporarily disabled since Snow Removal is hidden */}

                      {/* Bids Display - Comparison Table */}
                      <BidComparisonTable
                        bids={requestBids[request._id]}
                        request={request}
                        onAcceptBid={handleAcceptServiceBundle}
                        formatCurrency={formatCurrency}
                        renderStarRating={renderStarRating}
                        getServiceCategory={getServiceCategory}
                        getServiceName={getServiceName}
                        getFrequencyLabel={getFrequencyLabel}
                      />
                      
                      {/* Fallback for additional bids if more than 4 */}
                      {requestBids[request._id].length > 4 && (
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="h6" gutterBottom>
                            Additional Bids ({requestBids[request._id].length - 4} more)
                          </Typography>
                          <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                            {requestBids[request._id].slice(4).map((bid: any, index: number) => (
                              <Box
                                key={`${bid._id}-${index}`}
                                sx={{
                                  p: 2,
                                  mb: 2,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  backgroundColor: 'background.paper',
                                }}
                              >
                                {/* Provider Header */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                                  <Box>
                                    <Typography variant="h6" fontWeight="medium">
                                      {bid.providerId?.businessName || 'Provider'}
                                    </Typography>
                                    {bid.providerId?.rating && bid.providerId.rating > 0 ? (
                                      <Box sx={{ mt: 0.5 }}>
                                        {renderStarRating(
                                          bid.providerId.rating, 
                                          () => handleViewProviderRating(bid.providerId)
                                        )}
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        No reviews yet
                                      </Typography>
                                    )}
                                  </Box>
                                  <Chip
                                    label={bid.status === 'expired' ? 'EXPIRED' : (bid.status?.charAt(0).toUpperCase() + bid.status?.slice(1))}
                                    size="small"
                                    color={bid.status === 'expired' ? 'error' : (bid.status === 'pending' ? 'warning' : 'default')}
                                  />
                                </Box>

                                {/* Service Windows Layout */}
                                <Box sx={{ mb: 2 }}>
                                  {/* Group services by category */}
                                  {(() => {
                                    let lawnServices: any[] = [];
                                    let snowServices: any[] = [];
                                    let otherServices: any[] = [];
                                    
                                    // Check if bid has new servicePricing structure
                                    if (bid.servicePricing && bid.servicePricing.length > 0) {
                                      lawnServices = bid.servicePricing.filter((sp: any) => getServiceCategory(sp.service) === 'lawn');
                                      snowServices = bid.servicePricing.filter((sp: any) => getServiceCategory(sp.service) === 'snow');
                                      otherServices = bid.servicePricing.filter((sp: any) => getServiceCategory(sp.service) === 'other');
                                    } else {
                                      // Fallback for old bid format - create mock service pricing from request
                                      const requestedServices = request.serviceType?.name?.split(', ') || [];
                                      const serviceCount = requestedServices.length;
                                      const pricePerService = serviceCount > 0 ? bid.price / serviceCount : bid.price;
                                      const durationPerService = serviceCount > 0 ? bid.estimatedDuration / serviceCount : bid.estimatedDuration;
                                      
                                      requestedServices.forEach((service: string) => {
                                        const serviceCategory = getServiceCategory(service);
                                        const mockServicePricing = {
                                          service: service,
                                          serviceName: getServiceName(service),
                                          price: pricePerService,
                                          duration: durationPerService,
                                          frequency: request.frequency || 'weekly'
                                        };
                                        
                                        if (serviceCategory === 'lawn') {
                                          lawnServices.push(mockServicePricing);
                                        } else if (serviceCategory === 'snow') {
                                          snowServices.push(mockServicePricing);
                                        } else {
                                          otherServices.push(mockServicePricing);
                                        }
                                      });
                                    }
                                    
                                    return (
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {/* Service Windows Row */}
                                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                          {/* Lawn Services Window */}
                                          {lawnServices.length > 0 && (
                                            <Paper 
                                              elevation={2} 
                                              sx={{ 
                                                flex: 1, 
                                                minWidth: 280, 
                                                p: 2, 
                                                backgroundColor: 'success.50',
                                                border: 1,
                                                borderColor: 'success.200'
                                              }}
                                            >
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                <span style={{ fontSize: '1.5rem' }}>🌱</span>
                                                <Typography variant="h6" color="success.dark" fontWeight="medium">
                                                  Lawn Services
                                                </Typography>
                                              </Box>
                                              
                                              <Box sx={{ mb: 2 }}>
                                                {lawnServices.map((sp: any, index: number) => (
                                                  <Box key={index} sx={{ mb: 2, p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                                                    <Typography variant="body2" fontWeight="medium">
                                                      {sp.serviceName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                      {getFrequencyLabel(sp.frequency)}
                                                    </Typography>
                                                    <Typography variant="h6" color="success.dark" fontWeight="bold">
                                                      {formatCurrency(sp.price)}
                                                    </Typography>
                                                  </Box>
                                                ))}
                                              </Box>
                                              
                                              <Button
                                                variant="contained"
                                                color="success"
                                                fullWidth
                                                disabled={bid.status === 'expired'}
                                                onClick={() => handleAcceptServiceBundle(bid, lawnServices, request)}
                                                sx={bid.status === 'expired' ? { opacity: 0.6 } : {}}
                                              >
                                                {bid.status === 'expired' ? 'Expired - Cannot Accept' : 'Accept Lawn Services'}
                                              </Button>
                                            </Paper>
                                          )}
                                          
                                          {/* Snow Services Window - Temporarily hidden since Snow Removal is disabled */}
                                          
                                        </Box>
                                        
                                        {/* Accept All Button removed - Snow Removal is hidden, only Lawn Services available */}
                                      </Box>
                                    );
                                  })()}
                                </Box>

                                {/* Additional Notes */}
                                {bid.description && (() => {
                                  // Filter out the automatically generated service schedule text
                                  const cleanDescription = bid.description
                                    .replace(/Service Schedule & Pricing:[\s\S]*?(?=Additional Notes:|$)/, '')
                                    .replace(/Additional Notes:\s*/, '')
                                    .trim();
                                  
                                  // Only show if there's actual content after cleaning
                                  return cleanDescription && cleanDescription !== 'No additional notes provided.' ? (
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        "{cleanDescription}"
                                      </Typography>
                                    </Box>
                                  ) : null;
                                })()}
                                
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                      <Divider sx={{ my: 2 }} />
                    </Box>
                  )}

                  {/* Loading Bids */}
                  {loadingBids[request._id] && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" sx={{ ml: 1 }}>Loading bids...</Typography>
                    </Box>
                  )}

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* For pending or bidding status - show view bids button */}
                    {(request.status === 'pending' || request.status === 'bidding') && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleViewBids(request._id)}
                      >
                        {requestBids[request._id] && requestBids[request._id].length > 0 ? 'Hide Bids' : `View Bids (${bidCounts[request._id] ?? 0})`}
                      </Button>
                    )}
                    
                    {/* For accepted status - show service accepted status */}
                    {request.status === 'accepted' && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                      >
                        ✅ Service Accepted
                      </Button>
                    )}
                    
                    {/* For completed or cancelled status - just show disabled status */}
                    {(request.status === 'completed' || request.status === 'cancelled' || request.status === 'pending_cancellation') && (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled
                      >
                        {request.status === 'completed' ? 'Completed' : request.status === 'pending_cancellation' ? 'Pending Cancellation' : 'Cancelled'}
                      </Button>
                    )}
                    
                    {/* For expired_unaccepted status - show expired status */}
                    {request.status === 'expired_unaccepted' && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        disabled
                      >
                        Expired - No Bid Accepted
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            );
          })}
        </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={cancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Cancel Service Request
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="cancel-dialog-description">
            Are you sure you want to cancel the service request for "{deleteDialog.requestTitle}"?
            {(deleteDialog.requestStatus === 'pending' || deleteDialog.requestStatus === 'bidding') ? (
              <>
                {' '}Cancelling will remove any bids that have been placed for this request. If you submit a new request later, providers would need to submit new bids—previous bids will not be restored.
              </>
            ) : (
              <> Your provider will be notified immediately.</>
            )}
          </DialogContentText>
          {/* For accepted/scheduled services only: show "What happens" and reason field */}
          {deleteDialog.requestStatus && deleteDialog.requestStatus !== 'pending' && deleteDialog.requestStatus !== 'bidding' && (
            <>
              <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                <AlertTitle>What happens when you cancel:</AlertTitle>
                <Typography variant="body2" component="div">
                  • All providers who have submitted bids will be notified<br/>
                  • All bids will be cancelled<br/>
                  • Service records will be preserved for billing and legal purposes<br/>
                  • If you cancel after {cancellationCutoffText}, the upcoming service may still be chargeable
                </Typography>
              </Alert>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Reason for Cancellation (Optional)"
                placeholder="Please provide a reason for cancelling this service request..."
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                margin="normal"
                sx={{ mt: 1 }}
                helperText="This helps us improve our service"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} disabled={deleting}>
            Keep Request
          </Button>
          <Button 
            onClick={confirmCancel} 
            color="error" 
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Cancelling...' : 'Cancel Service Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Seasonal Contract Confirmation Dialog */}
      <Dialog
        open={seasonalContractConfirmDialog.open}
        onClose={() => setSeasonalContractConfirmDialog({ open: false, bid: null, request: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Confirm Seasonal Contract Terms</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>Important: Please Review Contract Terms</AlertTitle>
            <Typography variant="body2">
              You are about to accept a <strong>seasonal contract</strong>. Please carefully review the payment schedule and contract terms below before proceeding.
            </Typography>
          </Alert>

          {seasonalContractConfirmDialog.bid?.servicePricing && seasonalContractConfirmDialog.bid.servicePricing
            .filter((sp: any) => sp.isSeasonalContract || sp.frequency === 'seasonal-contract')
            .map((sp: any, idx: number) => {
              const seasonalDetails = sp.seasonalContractDetails;
              const paymentSchedule = seasonalDetails?.paymentSchedule || 'single-payment';
              
              // Calculate monthly payment info if monthly
              let monthlyPaymentInfo: { monthlyAmount: number; firstPaymentDate: Date; billingMonths: number } | null = null;
              if (paymentSchedule === 'monthly' && seasonalDetails?.totalContractPrice && seasonalDetails?.contractStartDate && seasonalDetails?.contractEndDate) {
                try {
                  const start = new Date(seasonalDetails.contractStartDate);
                  const end = new Date(seasonalDetails.contractEndDate);
                  const startsOnFirst = start.getUTCDate() === 1;
                  
                  let billingMonths: number;
                  if (startsOnFirst) {
                    billingMonths = (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + 
                                 (end.getUTCMonth() - start.getUTCMonth()) + 1;
                  } else {
                    const nextMonthYear = start.getUTCMonth() === 11 ? start.getUTCFullYear() + 1 : start.getUTCFullYear();
                    const nextMonthMonth = (start.getUTCMonth() + 1) % 12;
                    billingMonths = (end.getUTCFullYear() - nextMonthYear) * 12 + 
                                   (end.getUTCMonth() - nextMonthMonth) + 1;
                  }
                  
                  if (billingMonths > 0) {
                    const monthlyAmount = Math.round((seasonalDetails.totalContractPrice / billingMonths) * 100) / 100;
                    let firstPaymentDate: Date;
                    if (startsOnFirst) {
                      firstPaymentDate = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
                    } else {
                      const nextMonthYear = start.getUTCMonth() === 11 ? start.getUTCFullYear() + 1 : start.getUTCFullYear();
                      const nextMonthMonth = (start.getUTCMonth() + 1) % 12;
                      firstPaymentDate = new Date(Date.UTC(nextMonthYear, nextMonthMonth, 1));
                    }
                    monthlyPaymentInfo = { monthlyAmount, firstPaymentDate, billingMonths };
                  }
                } catch (e) {
                  console.error('[SEASONAL-CONFIRM] Error calculating monthly payment:', e);
                }
              }

              return (
                <Box key={idx} sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '2px solid', borderColor: 'primary.main' }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    {sp.serviceName}
                  </Typography>
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Total Contract Price:</strong> {formatCurrency(seasonalDetails?.totalContractPrice || 0)}
                    </Typography>
                    
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Service Frequency:</strong> {seasonalDetails?.serviceFrequency || 'weekly'}
                    </Typography>
                    
                    {seasonalDetails?.contractStartDate && seasonalDetails?.contractEndDate && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Contract Period:</strong> {formatUTCDateString(seasonalDetails.contractStartDate)} - {formatUTCDateString(seasonalDetails.contractEndDate)}
                      </Typography>
                    )}

                    <Box sx={{ mt: 2, p: 2, bgcolor: paymentSchedule === 'monthly' ? 'primary.light' : 'success.light', borderRadius: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                        Payment Schedule: {paymentSchedule === 'monthly' ? 'Monthly Payments' : 'One-Time Payment (Full payment after first service)'}
                      </Typography>
                      
                      {paymentSchedule === 'monthly' && monthlyPaymentInfo ? (
                        <>
                          <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
                            Monthly Amount: <strong>{formatCurrency(monthlyPaymentInfo.monthlyAmount)}</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            First Payment Date: <strong>{formatUTCDateString(monthlyPaymentInfo.firstPaymentDate)}</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Total Payments: <strong>{monthlyPaymentInfo.billingMonths} monthly payments</strong>
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                            Payments will be automatically charged on the <strong>1st of each month</strong> during the contract period.
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Payment Amount: <strong>{formatCurrency(seasonalDetails?.totalContractPrice || 0)}</strong>
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                            Full payment will be processed after the <strong>first service is completed</strong>.
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>By proceeding, you agree to:</strong>
            </Typography>
            <Typography variant="body2" component="ul" sx={{ mt: 1, pl: 2 }}>
              <li>The payment schedule shown above</li>
              <li>The contract period and service frequency</li>
              <li>All terms of the seasonal contract</li>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 'medium' }}>
              If the payment schedule is incorrect, please cancel and ask the provider to submit a new bid with the correct terms.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setSeasonalContractConfirmDialog({ open: false, bid: null, request: null })}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              // Close confirmation dialog and proceed to accept bid dialog
              setSeasonalContractConfirmDialog({ open: false, bid: null, request: null });
              setAcceptBidDialog({ open: true, bid: seasonalContractConfirmDialog.bid, request: seasonalContractConfirmDialog.request });
              setAgreedToTerms(false);
            }}
            variant="contained"
            color="primary"
          >
            I Confirm - Proceed to Accept Bid
          </Button>
        </DialogActions>
      </Dialog>

      {/* Accept Bid Dialog with Customer Agreement */}
      <Dialog
        open={acceptBidDialog.open}
        onClose={handleCancelAcceptBid}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          Accept Bid - Customer Service Agreement
        </DialogTitle>
        <DialogContent dividers>
          {acceptBidDialog.bid && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  You are about to accept a bid from <strong>{acceptBidDialog.bid.providerId?.businessName || 'Provider'}</strong>
                </Typography>
              </Alert>

              {/* Service Breakdown */}
              {acceptBidDialog.bid.servicePricing && acceptBidDialog.bid.servicePricing.length > 0 && (
                <Paper sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
                  <Typography variant="h6" gutterBottom>
                    Service Details
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {acceptBidDialog.bid.servicePricing.map((sp: any, index: number) => {
                      const isSeasonal = sp.isSeasonalContract || sp.frequency === 'seasonal-contract';
                      const seasonalDetails = sp.seasonalContractDetails;
                      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                      
                      return (
                        <Box key={index} sx={{ p: 2, backgroundColor: 'white', borderRadius: 1, border: 1, borderColor: 'grey.200' }}>
                          {/* Service Name and Price */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                            <Box>
                              <Typography variant="subtitle1" fontWeight="medium">
                                {sp.serviceName}
                              </Typography>
                              {sp.duration && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  Estimated Duration: {sp.duration} {sp.duration === 1 ? 'hour' : 'hours'}
                                </Typography>
                              )}
                            </Box>
                            <Typography variant="h6" color="primary" fontWeight="bold">
                              {isSeasonal && seasonalDetails?.totalContractPrice
                                ? formatCurrency(seasonalDetails.totalContractPrice)
                                : formatCurrency(sp.price)}
                            </Typography>
                          </Box>

                          <Divider sx={{ my: 1.5 }} />

                          {/* Frequency Details */}
                          <Box sx={{ mb: 1.5 }}>
                            <Typography variant="body2" fontWeight="medium" color="text.secondary" gutterBottom>
                              Service Frequency:
                            </Typography>
                            <Typography variant="body2" sx={{ pl: 1 }}>
                              {isSeasonal && seasonalDetails?.serviceFrequency
                                ? getFrequencyLabel(seasonalDetails.serviceFrequency)
                                : getFrequencyLabel(sp.frequency)}
                            </Typography>
                            {sp.recurringDayOfWeek !== undefined && (
                              <Typography variant="body2" color="text.secondary" sx={{ pl: 1, mt: 0.5 }}>
                                Scheduled for: {dayNames[sp.recurringDayOfWeek]}s
                              </Typography>
                            )}
                            {sp.recurringDayOfMonth !== undefined && (
                              <Typography variant="body2" color="text.secondary" sx={{ pl: 1, mt: 0.5 }}>
                                Scheduled for: Day {sp.recurringDayOfMonth} of each month
                              </Typography>
                            )}
                            {sp.biWeeklyStartDate && (
                              <Typography variant="body2" color="text.secondary" sx={{ pl: 1, mt: 0.5 }}>
                                Starting: {formatUTCDateString(sp.biWeeklyStartDate)}
                              </Typography>
                            )}
                          </Box>

                          {/* Payment Details */}
                          <Box>
                            <Typography variant="body2" fontWeight="medium" color="text.secondary" gutterBottom>
                              Payment Details:
                            </Typography>
                            {isSeasonal ? (
                              <Box sx={{ pl: 1 }}>
                                {seasonalDetails?.paymentSchedule === 'monthly' ? (
                                  <>
                                    <Typography variant="body2">
                                      Payment Schedule: <strong>Monthly Payments</strong>
                                    </Typography>
                                    {seasonalDetails.totalContractPrice && (
                                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        Monthly Amount: {formatCurrency(Math.round((seasonalDetails.totalContractPrice / 
                                          (seasonalDetails.contractStartDate && seasonalDetails.contractEndDate
                                            ? Math.max(1, Math.ceil((new Date(seasonalDetails.contractEndDate).getTime() - new Date(seasonalDetails.contractStartDate).getTime()) / (1000 * 60 * 60 * 24 * 30)))
                                            : 1)) * 100) / 100)}
                                      </Typography>
                                    )}
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                      First payment on the 1st of each month during contract period
                                    </Typography>
                                  </>
                                ) : (
                                  <>
                                    <Typography variant="body2">
                                      Payment Schedule: <strong>One-Time Payment</strong>
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                      Full payment processed after first service
                                    </Typography>
                                  </>
                                )}
                                {seasonalDetails?.contractStartDate && seasonalDetails?.contractEndDate && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Contract Period: {formatUTCDateString(seasonalDetails.contractStartDate)} - {formatUTCDateString(seasonalDetails.contractEndDate)}
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ pl: 1 }}>
                                Price per Service: <strong>{formatCurrency(sp.price)}</strong>
                              </Typography>
                            )}
                          </Box>

                          {/* Seasonal Contract Badge */}
                          {isSeasonal && (
                            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'grey.200' }}>
                              <Chip
                                label="Seasonal Contract"
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Customer Service Agreement
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Please read the following agreement carefully before proceeding:
              </Typography>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mt: 2,
                  mb: 3,
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.300',
                  maxHeight: 400,
                  overflow: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {customerAgreement || 'Loading agreement...'}
              </Paper>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    I have read and agree to the Customer Service Agreement and{' '}
                    <Link href="/privacy-policy" target="_blank" rel="noopener">
                      Privacy Policy
                    </Link>
                    . I understand that by clicking "{hasPaymentMethod === true ? 'Accept Bid & Continue' : 'Set Up Payment Method'}", 
                    I am entering into a binding agreement with the service provider{hasPaymentMethod === false ? ' and will set up automatic payment for services' : ''}.
                  </Typography>
                }
              />

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAcceptBid} disabled={acceptingBid}>
            Cancel
          </Button>
          <Button 
            onClick={handleProceedToPayment}
            variant="contained"
            color="primary"
            disabled={!agreedToTerms || acceptingBid}
          >
            {acceptingBid 
              ? 'Processing...' 
              : hasPaymentMethod === true 
                ? 'Accept Bid & Continue' 
                : hasPaymentMethod === false 
                  ? 'Set Up Payment Method' 
                  : 'Accept Bid & Continue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Provider Rating Dialog */}
      <ProviderRatingDialog
        open={ratingDialog.open}
        onClose={handleCloseRatingDialog}
        provider={ratingDialog.provider}
      />

      {/* Payment Method Setup Dialog */}
      <PaymentMethodSetup
        open={paymentSetupDialog.open}
        onClose={handlePaymentSetupClose}
        onSuccess={handlePaymentSetupSuccess}
        bidId={paymentSetupDialog.bidId}
        customerName={paymentSetupDialog.customerName}
        amount={paymentSetupDialog.amount}
        existingCustomerId={paymentSetupDialog.existingCustomerId} // NEW: Pass property-specific ID
      />

      {agreementDialog}
    </Container>
  );
};

export default MyRequestsPage;
